import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { delegationsApi } from '@/api/delegations';
import { employeesApi } from '@/api/employees';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  const axiosError = error as { response?: { data?: { message?: string } } };
  if (axiosError.response?.data?.message) {
    return axiosError.response.data.message;
  }
  return 'Произошла ошибка';
}

export function useClientAccess(clientId: number | undefined) {
  return useQuery({
    queryKey: ['delegations', 'access', clientId],
    queryFn: () => delegationsApi.getClientAccess(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

export function useAvailableRecipients() {
  return useQuery({
    queryKey: ['employees', 'for-selector'],
    queryFn: () => employeesApi.getForSelector(false), // only active
    staleTime: 60_000,
  });
}

export function useCreateDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      clientId: number;
      toEmployeeId: string;
      expiresAt: string;
    }) => delegationsApi.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
      queryClient.invalidateQueries({ queryKey: ['client-indicators'] });
      if (data.wasAdjusted) {
        toast.warning('Делегирование создано', {
          description: `Срок скорректирован до ${new Date(data.expiresAt).toLocaleDateString('ru')}`,
        });
      } else {
        toast.success('Делегирование создано');
      }
    },
    onError: (error) => {
      toast.error('Ошибка создания делегирования', {
        description: getErrorMessage(error),
      });
    },
  });
}

export function useUpdateDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, expiresAt }: { id: string; expiresAt: string }) =>
      delegationsApi.update(id, { expiresAt }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
      queryClient.invalidateQueries({ queryKey: ['client-indicators'] });
      if (data.wasAdjusted) {
        toast.warning('Делегирование обновлено', {
          description: `Срок скорректирован до ${new Date(data.expiresAt).toLocaleDateString('ru')}`,
        });
      } else {
        toast.success('Делегирование обновлено');
      }
    },
    onError: (error) => {
      toast.error('Ошибка обновления делегирования', {
        description: getErrorMessage(error),
      });
    },
  });
}

export function useDeleteDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => delegationsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
      queryClient.invalidateQueries({ queryKey: ['client-indicators'] });
      toast.success('Делегирование удалено');
    },
    onError: (error) => {
      toast.error('Ошибка удаления делегирования', {
        description: getErrorMessage(error),
      });
    },
  });
}
